import { IconChevronDown, IconChevronRight, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import { Badge, Input, ListaControlesSkeleton, Select, Switch, Text } from "@/components/ui";
import { cn } from "@/lib/cn";
import type { ConfigColumna, ConfigTabla } from "../services/admin-reporteria.api";
import { useIntrospeccionColumnas, useIntrospeccionTablas } from "../hooks/use-admin-reporteria";

/**
 * Editor de tablas/columnas habilitadas a partir de la INTROSPECCIÓN del catálogo
 * real (§5.3, §8.2). Por columna se marca el enmascarado (+ máscara) y el override
 * "mostrar aunque parezca id" (RN-20).
 */
export function TablasColumnasEditor({
  tablas,
  onChange,
}: {
  tablas: ConfigTabla[];
  onChange: (tablas: ConfigTabla[]) => void;
}) {
  const disponibles = useIntrospeccionTablas();

  const yaAgregada = (esquema: string, tabla: string) =>
    tablas.some((t) => t.esquemaTabla === esquema && t.nombreTabla === tabla);

  const opciones = (disponibles.data ?? [])
    .filter((t) => !yaAgregada(t.esquema, t.tabla))
    .map((t) => ({ value: `${t.esquema}|${t.tabla}`, label: `${t.esquema}.${t.tabla}` }));

  function agregar(valor: string) {
    const [esquema, tabla] = valor.split("|");
    onChange([...tablas, { esquemaTabla: esquema, nombreTabla: tabla, etiqueta: null, columnas: [] }]);
  }

  function actualizar(idx: number, tabla: ConfigTabla) {
    onChange(tablas.map((t, i) => (i === idx ? tabla : t)));
  }

  return (
    <div className="space-y-2">
      <div className="w-72">
        <Select
          size="sm"
          placeholder={disponibles.isLoading ? "Cargando catálogo…" : "Agregar tabla…"}
          value=""
          onValueChange={agregar}
          options={opciones}
          searchable
          searchPlaceholder="Buscar tabla…"
        />
      </div>

      {tablas.length === 0 && (
        <Text size="sm" c="dimmed">
          Agrega al menos una tabla para habilitar columnas.
        </Text>
      )}

      {tablas.map((t, i) => (
        <TablaColumnasCard
          key={`${t.esquemaTabla}.${t.nombreTabla}`}
          tabla={t}
          onChange={(nt) => actualizar(i, nt)}
          onRemove={() => onChange(tablas.filter((_, idx) => idx !== i))}
        />
      ))}
    </div>
  );
}

function TablaColumnasCard({
  tabla,
  onChange,
  onRemove,
}: {
  tabla: ConfigTabla;
  onChange: (t: ConfigTabla) => void;
  onRemove: () => void;
}) {
  const [abierta, setAbierta] = useState(true);
  const columnas = useIntrospeccionColumnas(tabla.esquemaTabla, tabla.nombreTabla);

  const habilitada = (nombre: string) => tabla.columnas.find((c) => c.nombreColumna === nombre);

  function toggle(nombre: string, tipoDato: string) {
    const existe = habilitada(nombre);
    if (existe) {
      onChange({ ...tabla, columnas: tabla.columnas.filter((c) => c.nombreColumna !== nombre) });
    } else {
      const nueva: ConfigColumna = {
        nombreColumna: nombre,
        etiqueta: null,
        tipoDato,
        enmascarar: false,
        mascara: null,
        mostrarSiId: false,
      };
      onChange({ ...tabla, columnas: [...tabla.columnas, nueva] });
    }
  }

  function actualizarCol(nombre: string, patch: Partial<ConfigColumna>) {
    onChange({
      ...tabla,
      columnas: tabla.columnas.map((c) => (c.nombreColumna === nombre ? { ...c, ...patch } : c)),
    });
  }

  return (
    <div className="rounded-md border border-border">
      <div className="flex items-center gap-2 px-2 py-1.5">
        <button type="button" onClick={() => setAbierta((v) => !v)} className="text-muted-foreground">
          {abierta ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
        </button>
        <span className="font-medium">
          {tabla.esquemaTabla}.{tabla.nombreTabla}
        </span>
        <Badge size="xs" variant="light">
          {tabla.columnas.length}
        </Badge>
        <Input
          className="ml-2 w-48"
          size="sm"
          placeholder="Etiqueta de la tabla (opcional)"
          value={tabla.etiqueta ?? ""}
          onChange={(e) => onChange({ ...tabla, etiqueta: e.currentTarget.value || null })}
        />
        <button type="button" onClick={onRemove} className="ml-auto text-muted-foreground hover:text-foreground" aria-label="Quitar tabla">
          <IconTrash size={16} />
        </button>
      </div>

      {abierta && (
        <div className="border-t border-border p-2">
          {columnas.isLoading ? (
            <ListaControlesSkeleton filas={5} control="checkbox" className="gap-2 px-2 py-1" etiqueta="Cargando columnas…" />
          ) : (
            <div className="space-y-1">
              {(columnas.data ?? []).map((col) => {
                const cfg = habilitada(col.columna);
                return (
                  <div
                    key={col.columna}
                    className={cn(
                      "rounded-md px-2 py-1",
                      cfg ? "bg-multiva-50 dark:bg-multiva-900/20" : "hover:bg-muted"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!cfg}
                          onChange={() => toggle(col.columna, col.tipoDato)}
                          className="h-4 w-4 accent-multiva-600"
                        />
                        <span className="truncate text-sm">{col.columna}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">{col.tipoDato}</span>
                      </label>
                      {cfg && (
                        <Input
                          size="sm"
                          className="w-40"
                          placeholder="Etiqueta"
                          value={cfg.etiqueta ?? ""}
                          onChange={(e) => actualizarCol(col.columna, { etiqueta: e.currentTarget.value || null })}
                        />
                      )}
                    </div>
                    {cfg && (
                      <div className="mt-1 flex flex-wrap items-center gap-x-5 gap-y-1 pl-6">
                        <Switch
                          checked={cfg.enmascarar}
                          onCheckedChange={(v) => actualizarCol(col.columna, { enmascarar: v })}
                          label="Enmascarar"
                        />
                        {cfg.enmascarar && (
                          <Input
                            size="sm"
                            className="w-44"
                            placeholder="Máscara (vacío = últimos 4)"
                            value={cfg.mascara ?? ""}
                            onChange={(e) => actualizarCol(col.columna, { mascara: e.currentTarget.value || null })}
                          />
                        )}
                        <Switch
                          checked={cfg.mostrarSiId}
                          onCheckedChange={(v) => actualizarCol(col.columna, { mostrarSiId: v })}
                          label="Mostrar aunque parezca id"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
